import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";

import { messaging } from "JS/Helpers/UserMessaging";
import { AppTypography } from "JS/React/Components/AppTypography";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useDocumentsFavoritesDetails } from "JS/React/Hooks/Documents";
import { useRouting } from "JS/React/Hooks/Routes";

const Root = styled("div")({});

const ItemContainer = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1),

    "&:hover": {
        background: theme.palette.grey["200"],
        cursor: "pointer",
    },
}));

const StyledIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "35px",
    [theme.breakpoints.down("md")]: {
        fontSize: "22px",
    },
}));

const PdfIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: "red",
    margin: theme.spacing(0, 2),
    fontSize: "2rem",
    [theme.breakpoints.down("md")]: {
        fontSize: "22px",
    },
}));

const Description = styled(AppTypography)(({ theme }) => ({
    fontSize: "30px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.grey[600],
    [theme.breakpoints.down("md")]: {
        fontSize: "20px",
    },
    [theme.breakpoints.down("sm")]: {
        fontSize: "18px",
    },
}));

const Content = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: `calc(100vh - ${theme.footer.height})`,
    width: "100%",
    color: theme.palette.common.black,
}));

export interface DocumentFavListProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const DocumentFavList = (props: DocumentFavListProps) => {
    const { className, ...rest } = props;

    const navigate = useNavigate();
    const { linkProvider } = useRouting();

    const { docDetails, loading: docDetailsLoading } =
        useDocumentsFavoritesDetails();

    const onDocumentClick = (pdfName: string, description: string) => {
        navigate(
            linkProvider.react.documents().detail(pdfName, description, "true"),
        );
    };

    return (
        <Root {...rest} className={className}>
            {docDetailsLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.documents}
                />
            )}
            {!docDetailsLoading && docDetails?.length === 0 && (
                <Content align="center" variant="h6">
                    {messaging.common.noItemsFound}
                </Content>
            )}

            {docDetails?.length !== 0 &&
                docDetails?.map((d, idx) => (
                    <ItemContainer
                        key={idx}
                        container
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        onClick={() => onDocumentClick(d.Name, d.Description)}
                    >
                        <Grid
                            item
                            sm={11}
                            xs={10}
                            display={"flex"}
                            alignItems={"center"}
                        >
                            <PdfIcon icon={["fas", "file-pdf"]} />
                            <Description variant="h4">
                                {d.Description}
                            </Description>
                        </Grid>
                        <Grid
                            item
                            sm={1}
                            xs={2}
                            display={"flex"}
                            justifyContent={"center"}
                        >
                            <StyledIcon icon={["fas", "chevron-right"]} />
                        </Grid>
                    </ItemContainer>
                ))}
        </Root>
    );
};
